import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card } from '../../../../../Common/Card'
import { Flex } from '../../../../../Common/Flex'
import { useWindowDimensions } from '../../../../../Common/Hooks/useWindowDimensions'
import { Layout } from '../../../../../Common/Layout'
import { H3, H4, Text } from '../../../../../Common/Typography'
import { IAbsenceNew } from '../../../../AbsenceNew/IAbsenceNew'
import { IAbsenceFormBase } from '../../IAbsenceFormBase'
import { CrudButtons } from '../CrudButtons'

import { DeleteOverlay } from './DeleteOverlay'
import {
  CalendarContainer,
  CalendarIcon,
  CalendarIconContainer,
  CalendarIconTime,
  IconEm,
  IconSpan,
  IconStrong,
  IconText,
} from './style'

type Props = {
  isRefetching: boolean
} & (AbsenceCardProps | GeneralAbsenceCardProps)

type AbsenceCardProps = {
  absenceInfo: IAbsenceNew
  pageType: 'absence'
}

type GeneralAbsenceCardProps = {
  absenceInfo: IAbsenceFormBase
  pageType: 'general-absence'
}

export const BaseAbsenceCard = ({
  absenceInfo,
  isRefetching,
  pageType,
}: Props) => {
  const [isOverlayActive, setIsOverlayActive] = useState<boolean>(false)
  const [isInactiveCrudButtons] = useState<boolean>(
    pageType === 'general-absence' ? false : absenceInfo.isGeneralAbsence,
  )
  const { windowWidth } = useWindowDimensions()
  const { t } = useTranslation()

  const slug = pageType === 'absence' ? 'absences' : 'general-absences'

  const isSameDay = moment(absenceInfo.startsAt).isSame(
    absenceInfo.endsAt,
    'day',
  )

  return (
    <Layout>
      <Card
        showSkeleton={isRefetching}
        position={'relative'}
        flexDirection={windowWidth < 900 ? 'column' : 'row'}
        alignItems={'center'}
        bg="lights.3"
        border
        p={3}
        m={1}
        style={{ gap: '20px' }}
        shadow>
        <CalendarIconContainer alignItems={'center'}>
          <CalendarContainer flexDirection={'column'}>
            <H4 textAlign={'center'} color={'typography.0'}>
              {moment(absenceInfo.startsAt).format('YYYY')}
            </H4>
            <CalendarIconTime className="icon">
              <IconStrong>
                {moment(absenceInfo.startsAt).format('MMM').toUpperCase()}
              </IconStrong>
              <IconText>
                <IconEm>{moment(absenceInfo.startsAt).format('dddd')}</IconEm>
                <IconSpan>{moment(absenceInfo.startsAt).format('D')}</IconSpan>
              </IconText>
            </CalendarIconTime>
          </CalendarContainer>

          {!isSameDay && (
            <>
              <CalendarIcon icon={'arrow-right'} />
              <CalendarContainer flexDirection={'column'}>
                <H4 textAlign={'center'} color={'typography.0'}>
                  {moment(absenceInfo.endsAt).format('YYYY')}
                </H4>
                <CalendarIconTime className="icon">
                  <IconStrong>
                    {moment(absenceInfo.endsAt).format('MMM').toUpperCase()}
                  </IconStrong>
                  <IconText>
                    <IconEm>{moment(absenceInfo.endsAt).format('dddd')}</IconEm>
                    <IconSpan>
                      {moment(absenceInfo.endsAt).format('D')}
                    </IconSpan>
                  </IconText>
                </CalendarIconTime>
              </CalendarContainer>
            </>
          )}
        </CalendarIconContainer>

        <Flex flexGrow={3} flexDirection="column" pl={2} pt={'24px'}>
          <H3 mb={1}>{t(`absence.${absenceInfo.absenceType.key}`)}</H3>
          <Text color="typography.4">{absenceInfo.reason}</Text>
        </Flex>

        <Flex
          alignItems={'center'}
          justifyContent={'flex-end'}
          flexGrow={1}
          style={{ gap: '10px' }}>
          <CrudButtons
            isRefetching={isRefetching}
            absenceInfo={absenceInfo}
            isInactiveCrudButtons={isInactiveCrudButtons}
            slug={slug}
            setIsOverlayActive={setIsOverlayActive}
          />
        </Flex>
        {!isOverlayActive || (
          <DeleteOverlay
            type={pageType}
            setIsOverlayActive={setIsOverlayActive}
            absenceUuid={absenceInfo.uuid}
            isInactiveCrudButtons={isInactiveCrudButtons}
          />
        )}
      </Card>
    </Layout>
  )
}
